export function lightenColor(color, percent) {
  let r = parseInt(color.substring(1, 3), 16);
  let g = parseInt(color.substring(3, 5), 16);
  let b = parseInt(color.substring(5, 7), 16);

  r = Math.min(255, r + (255 - r) * (percent / 100));
  g = Math.min(255, g + (255 - g) * (percent / 100));
  b = Math.min(255, b + (255 - b) * (percent / 100));

  return `#${Math.round(r).toString(16).padStart(2, "0")}${Math.round(g)
    .toString(16)
    .padStart(2, "0")}${Math.round(b).toString(16).padStart(2, "0")}`;
}

/** Darken a hex color toward black by percent (0–100). */
export function darkenColor(color, percent) {
  let r = parseInt(color.substring(1, 3), 16);
  let g = parseInt(color.substring(3, 5), 16);
  let b = parseInt(color.substring(5, 7), 16);
  const factor = 1 - Math.min(100, Math.max(0, percent)) / 100;
  r = Math.max(0, Math.round(r * factor));
  g = Math.max(0, Math.round(g * factor));
  b = Math.max(0, Math.round(b * factor));
  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/** Stocking scatter points in Plotly V2 charts (distinct from sampling; matches stocking bar tone). */
export const STOCKING_SCATTER_COLOR = "#7c3aed";

export function getColorForSpecies(species, speciesList) {
  const prominentColors = [
    "#E6194B",
    "#3CB44B",
    "#0082C8",
    "#F58231",
    "#911EB4",
    "#46F0F0",
    "#F032E6",
    "#D2F53C",
    "#800000",
    "#857403",
  ];

  const speciesIndex = speciesList.indexOf(species);
  return prominentColors[speciesIndex % prominentColors.length];
}

/** Match `species` labels produced in individual-tag graph mode, e.g. `Tag LT1025`. */
export function tagIdFromSpeciesLabel(species) {
  const m = String(species ?? "").match(/^Tag\s+(.+)$/i);
  return m ? m[1].trim() : null;
}

/**
 * Legend / trace order for weight-vs-length: tags first (numeric tag id), then other species A–Z.
 * @param {string[]} speciesList
 * @returns {string[]}
 */
export function sortSpeciesForWeightLengthLegend(speciesList) {
  const tags = [];
  const rest = [];
  for (const s of speciesList) {
    if (tagIdFromSpeciesLabel(s)) tags.push(s);
    else rest.push(s);
  }
  tags.sort((a, b) =>
    (tagIdFromSpeciesLabel(a) || "").localeCompare(tagIdFromSpeciesLabel(b) || "", undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
  rest.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
  return [...tags, ...rest];
}

/**
 * Maximally distinct color palette - colors chosen to be visually far apart.
 * Colors ordered to maximize perceptual distance between consecutive entries.
 * Optimized for distinguishability even for colorblind viewers.
 */
const MAXIMALLY_DISTINCT_COLORS = [
  "#870364", // Plum
  "#048c32", // Green
  "#9e9c05", // Yellow
  "#b86d04", // Orange
  "#8f0303", // Red
  "#FF0000", // Pure Red
  "#00FF00", // Pure Green  
  "#0000FF", // Pure Blue
  "#FF00FF", // Pure Magenta
  "#00FFFF", // Pure Cyan
  "#FF8000", // Orange
  "#8000FF", // Purple
  "#0080FF", // Azure
  "#909191", // Gray
  "#000000", // Black
];

/**
 * Plotly marker shapes in order of visual distinction.
 */
const MARKER_SHAPES = [
  "circle",
  "square", 
  "diamond",
  "cross",
  "x",
  "triangle-up",
  "triangle-down",
  "pentagon",
  "hexagon",
  "star",
];

/**
 * Indices into {@link MAXIMALLY_DISTINCT_COLORS} for tag ranks 0,1,…,14 (each 0–14 appears once).
 * Order is hand-tuned so consecutive sorted tags (LT1000, LT1001, …) step through contrasting
 * swatches from the same fifteen-hex list only (no generated colors).
 */
const TAG_SERIES_PALETTE_INDEX_ORDER = Object.freeze([
  5, 9, 7, 2, 11, 1, 10, 12, 8, 3, 13, 0, 6, 4, 14,
]);

/**
 * Color+shape for the i-th tag in a fixed ordered list of `tagCount` tag series.
 *
 * - **tagCount ≤ 15:** only {@link MAXIMALLY_DISTINCT_COLORS}; uses {@link TAG_SERIES_PALETTE_INDEX_ORDER}
 *   with spread across ranks when fewer than 15 tags.
 * - **tagCount > 15:** {@link MAXIMALLY_DISTINCT_COLORS} × {@link MARKER_SHAPES} matrix.
 */
function getColorAndShapeForOrderedTagIndex(tagIndex, tagCount) {
  const i = Number.isFinite(tagIndex) ? Math.max(0, Math.floor(tagIndex)) : 0;
  const n = Number.isFinite(tagCount) ? Math.max(1, Math.floor(tagCount)) : 1;
  const numColors = MAXIMALLY_DISTINCT_COLORS.length;
  const numShapes = MARKER_SHAPES.length;
  const orderLen = TAG_SERIES_PALETTE_INDEX_ORDER.length;

  if (n <= orderLen) {
    const maxIdx = orderLen - 1;
    const pos = n <= 1 ? 0 : Math.round((i / (n - 1)) * maxIdx);
    const paletteSlot = TAG_SERIES_PALETTE_INDEX_ORDER[pos];
    return {
      color: MAXIMALLY_DISTINCT_COLORS[paletteSlot],
      shape: MARKER_SHAPES[0],
    };
  }

  const colorIndex = i % numColors;
  const shapeIndex = Math.floor(i / numColors) % numShapes;
  return {
    color: MAXIMALLY_DISTINCT_COLORS[colorIndex],
    shape: MARKER_SHAPES[shapeIndex],
  };
}

/**
 * Get color and shape for a tag series using color×shape matrix strategy.
 * First exhaust all colors with one shape, then move to next shape.
 * E.g., tags 0-14 use colors 0-14 with circles, tags 15-29 use colors 0-14 with squares, etc.
 *
 * Prefer {@link getWeightLengthSeriesColor} for chart legends (passes group size for ≤15 permutation).
 *
 * @param {number} index - series index (0-based)
 * @returns {{ color: string, shape: string }}
 */
export function getColorAndShapeForTag(index) {
  const i = Number.isFinite(index) ? Math.max(0, Math.floor(index)) : 0;
  const numColors = MAXIMALLY_DISTINCT_COLORS.length;
  const numShapes = MARKER_SHAPES.length;

  const colorIndex = i % numColors;
  const shapeIndex = Math.floor(i / numColors) % numShapes;

  return {
    color: MAXIMALLY_DISTINCT_COLORS[colorIndex],
    shape: MARKER_SHAPES[shapeIndex],
  };
}

/**
 * Get just the color (backward compatibility for charts that don't use shapes).
 * @param {number} index - 0 .. n-1
 * @param {number} _total - reserved for future tuning
 */
export function getQualitativeDistinctColor(index, _total) {
  const { color } = getColorAndShapeForTag(index);
  return color;
}

/** Length of {@link MAXIMALLY_DISTINCT_COLORS} — soft cap for solid bar/line series. */
export const MAXIMALLY_DISTINCT_COLOR_COUNT = MAXIMALLY_DISTINCT_COLORS.length;

/** N-th color from the shared maximally-distinct palette (used by dates, bins, etc.). */
export function getPaletteColorAt(index) {
  const n = MAXIMALLY_DISTINCT_COLOR_COUNT;
  const i = Number.isFinite(index) ? Math.max(0, Math.floor(index)) : 0;
  return MAXIMALLY_DISTINCT_COLORS[i % n];
}

/**
 * Marker outline color that contrasts with fill (Plotly `marker.line.color`).
 * Helps overlapping/similar fills remain readable on dense scatter plots.
 * @param {string} fillHex - "#RGB" or "#RRGGBB"
 * @returns {string}
 */
export function contrastingStrokeForFill(fillHex) {
  const raw = String(fillHex ?? "").trim();
  const hex = raw.startsWith("#") ? raw.slice(1) : raw;
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(hex)) return "#111827";
  const full =
    hex.length === 3 ? [...hex].map((c) => c + c).join("") : hex;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? "#111827" : "#ffffff";
}

/**
 * Plotly scatter: resolved color + symbol for any `species` label (tags use color×shape matrix).
 * @returns {{ color: string, symbol: string }}
 */
export function getPlotlyLegendStyle(species, orderedSpecies) {
  const v = getWeightLengthSeriesColor(species, orderedSpecies);
  if (v && typeof v === "object" && v.color) {
    return { color: v.color, symbol: v.shape || "circle" };
  }
  return {
    color: typeof v === "string" ? v : "#888888",
    symbol: "circle",
  };
}

/**
 * Distinct fill color for the i-th of n series (short fixed palette, then HSL fallback).
 * Prefer {@link getQualitativeDistinctColor} for many categorical tag-like series.
 * @param {number} index - 0 .. n-1
 * @param {number} total
 */
export function getColorForSeriesIndex(index, total) {
  const distinctTagPalette = [
    "#e6194b",
    "#3cb44b",
    "#4363d8",
    "#f58231",
    "#911eb4",
    "#46f0f0",
    "#f032e6",
    "#bcf60c",
    "#fabebe",
    "#008080",
    "#e6beff",
    "#9a6324",
    "#fffac8",
    "#800000",
    "#aaffc3",
    "#808000",
    "#ffd8b1",
    "#000075",
    "#808080",
    "#1f77b4",
    "#d62728",
    "#2ca02c",
    "#ff7f0e",
    "#17becf",
    "#9467bd",
    "#8c564b",
    "#7f7f7f",
    "#aec7e8",
    "#98df8a",
    "#ff9896",
    "#c5b0d5",
    "#c49c94",
  ];
  const i = Number.isFinite(index) ? Math.max(0, Math.floor(index)) : 0;
  if (i < distinctTagPalette.length) return distinctTagPalette[i];

  const n = Math.max(1, total);
  const hue = ((360 * (i - distinctTagPalette.length)) / n + 17) % 360;
  return `hsl(${Math.round(hue)}, 72%, 42%)`;
}

/**
 * Get color+shape for a weight-vs-length tag series; color only for non-tag species.
 * @param {string} species
 * @param {string[]} orderedSpecies - already sorted for legend
 * @returns {string | {color: string, shape: string}} - object for tags, string for species
 */
export function getWeightLengthSeriesColor(species, orderedSpecies) {
  const tagId = tagIdFromSpeciesLabel(species);
  if (tagId) {
    const tagIds = orderedSpecies
      .map((s) => tagIdFromSpeciesLabel(s))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
    const uniqueTagIds = [...new Set(tagIds)];
    const i = uniqueTagIds.indexOf(tagId);
    const idx = i >= 0 ? i : 0;
    return getColorAndShapeForOrderedTagIndex(idx, uniqueTagIds.length);
  }
  const nonTags = orderedSpecies.filter((s) => !tagIdFromSpeciesLabel(s));
  return getColorForSpecies(species, nonTags.length ? nonTags : orderedSpecies);
}

/** Map Plotly-style symbol names to Recharts Scatter `shape` (limited set). */
const PLOTLY_TO_RECHARTS_SCATTER_SHAPE = {
  circle: "circle",
  square: "square",
  diamond: "diamond",
  cross: "cross",
  x: "cross",
  "triangle-up": "triangle",
  "triangle-down": "triangle",
  pentagon: "star",
  hexagon: "square",
  star: "star",
};

export function rechartsScatterShapeFromPlotly(plotlyShape) {
  return PLOTLY_TO_RECHARTS_SCATTER_SHAPE[plotlyShape] || "circle";
}

/**
 * Recharts weight-vs-length: one fill + one shape per series (tags use color×shape matrix; species = circle).
 * @returns {{ fill: string, shape: string }}
 */
export function getWeightLengthRechartsScatterProps(species, orderedSpecies) {
  const v = getWeightLengthSeriesColor(species, orderedSpecies);
  if (v && typeof v === "object" && v.color) {
    return {
      fill: v.color,
      shape: rechartsScatterShapeFromPlotly(v.shape),
    };
  }
  return {
    fill: typeof v === "string" ? v : "#888888",
    shape: "circle",
  };
}

export function getWrColorForSpecies(species, speciesList) {
  const wrPalette = [
    "#4E79A7",
    "#F28E2B",
    "#E15759",
    "#76B7B2",
    "#59A14F",
    "#EDC949",
    "#AF7AA1",
    "#FF9DA7",
    "#9C755F",
    "#BAB0AC",
  ];
  const idx = speciesList.indexOf(species);
  return wrPalette[(idx >= 0 ? idx : 0) % wrPalette.length];
}

/**
 * Distinct colors for sampling sessions (by-date bars, weight-vs-length-by-date, etc.).
 * Uses {@link MAXIMALLY_DISTINCT_COLORS} only (no other palette).
 *
 * - **At most 15** distinct keys: each date gets a color from that list; indices are **spread**
 *   across the full palette (first → last slot) so consecutive dates are less likely to pick
 *   adjacent similar hues (e.g. plum vs dark red).
 * - **More than 15** keys: same cycling as {@link getPaletteColorAt} by chronological index.
 *
 * @param {string} dateKey
 * @param {string[]} orderedDateKeys - sorted distinct keys, same order used by the chart
 */
export function getColorForDate(dateKey, orderedDateKeys) {
  const keys = Array.isArray(orderedDateKeys) ? orderedDateKeys : [];
  const n = keys.length;
  const idx = keys.indexOf(dateKey);
  const i = idx >= 0 ? idx : 0;
  const paletteLen = MAXIMALLY_DISTINCT_COLORS.length;

  if (n <= 0) {
    return MAXIMALLY_DISTINCT_COLORS[0];
  }
  if (n === 1) {
    return MAXIMALLY_DISTINCT_COLORS[0];
  }
  if (n <= paletteLen) {
    const maxIdx = paletteLen - 1;
    const paletteIndex = Math.round((i / (n - 1)) * maxIdx);
    return MAXIMALLY_DISTINCT_COLORS[paletteIndex];
  }
  return getPaletteColorAt(i);
}
